<template>
  <div class="telesales-report py-3">
    <header class="ts-toolbar">
      <div class="ts-toolbar__title">
        <span class="ts-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Telesales Report</h1>
          <p>Daily call volume per agent, with totals and conversion</p>
        </div>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="ts-alert">{{ errorMessage }}</CAlert>

    <section class="ts-section">
      <header class="ts-section__head">
        <div class="ts-section__head-left">
          <h2>Filters</h2>
          <small class="ts-section__hint">{{ rangeLabel }}</small>
        </div>
        <button
          type="button"
          class="ts-btn ts-btn--primary"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadReport"
        >
          <svg v-if="!isLoading" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M3 3v18h18" />
            <path d="M7 14l4-4 4 4 5-6" />
          </svg>
          <span>{{ isLoading ? 'Loading…' : 'Load Report' }}</span>
        </button>
      </header>
      <div class="ts-section__body">
        <div class="ts-filters">
          <div class="ts-field ts-field--range">
            <label class="ts-label">Period</label>
            <DateRangePicker v-model="dateRange" variant="form" :presets="false" />
          </div>
          <div class="ts-field ts-field--presets">
            <label class="ts-label" aria-hidden="true">&nbsp;</label>
            <div class="ts-presets" role="group" aria-label="Quick date ranges">
              <button
                v-for="preset in presets"
                :key="preset.key"
                type="button"
                class="ts-preset"
                :class="{ 'is-active': activePreset === preset.key }"
                @click="applyPreset(preset.key)"
              >
                {{ preset.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div v-if="summary" class="ts-kpis">
      <div class="kpi-tile kpi-tile--calls">
        <span class="kpi-tile__label">Calls</span>
        <span class="kpi-tile__value">{{ formatNumber(summary.calls_total) }}</span>
        <span class="kpi-tile__sub">{{ rangeLabel }}</span>
      </div>
      <div class="kpi-tile kpi-tile--orders">
        <span class="kpi-tile__label">Orders</span>
        <span class="kpi-tile__value">{{ formatNumber(summary.orders_total) }}</span>
        <span class="kpi-tile__sub">{{ ordersPerDayLabel }}</span>
      </div>
      <div class="kpi-tile kpi-tile--bottles">
        <span class="kpi-tile__label">Bottles</span>
        <span class="kpi-tile__value">{{ formatNumber(summary.bottles_total) }}</span>
        <span class="kpi-tile__sub">{{ bottlesPerOrderLabel }}</span>
      </div>
      <div class="kpi-tile kpi-tile--conversion">
        <span class="kpi-tile__label">Conversion</span>
        <span class="kpi-tile__value">{{ formatPercent(summary.conversion_total) }}</span>
        <span class="kpi-tile__sub">Orders ÷ calls</span>
      </div>
    </div>

    <BaseTable
      title="Calls by Agent · Daily"
      :items="tableRows"
      :columns="columns"
      :loading="isLoading"
      :show-count="false"
      empty-text="No calls in this range."
    >
      <template #toolbar>
        <span v-if="callsRows.length" class="ts-totals">
          <span class="ts-totals__chip">{{ callsRows.length }} day{{ callsRows.length === 1 ? '' : 's' }}</span>
          <span v-if="topAgent" class="ts-totals__chip ts-totals__chip--brand">Top agent <strong>{{ topAgent.name }}</strong> · {{ formatNumber(topAgent.total) }} calls</span>
        </span>
      </template>

      <template #cell-_total="{ value }">
        <strong class="ts-total-cell">{{ formatNumber(value) }}</strong>
      </template>
    </BaseTable>
  </div>
</template>

<script>
import axios from 'axios'
import DateRangePicker from '@/components/DateRangePicker.vue'
import BaseTable from '@/components/BaseTable.vue'

export default {
  name: 'TelesalesReport',
  components: { DateRangePicker, BaseTable },
  data () {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const sevenAgo = new Date(today)
    sevenAgo.setDate(today.getDate() - 6)
    return {
      fromDate: this.toIsoStatic(sevenAgo),
      toDate: this.toIsoStatic(today),
      summary: null,
      callsRows: [],
      userNames: [],
      isLoading: false,
      errorMessage: '',
      presets: [
        { key: 'today', label: 'Today' },
        { key: '7d', label: 'Last 7 Days' },
        { key: '30d', label: 'Last 30 Days' },
        { key: 'mtd', label: 'This Month' },
        { key: 'ytd', label: 'This Year' }
      ]
    }
  },
  computed: {
    dateRange: {
      get () {
        return { fromDate: this.fromDate, toDate: this.toDate }
      },
      set (value) {
        this.fromDate = (value && value.fromDate) || ''
        this.toDate = (value && value.toDate) || ''
      }
    },
    rangeLabel () {
      if (!this.fromDate || !this.toDate) return ''
      if (this.fromDate === this.toDate) return this.formatDisplay(this.fromDate)
      return `${this.formatDisplay(this.fromDate)} → ${this.formatDisplay(this.toDate)}`
    },
    activePreset () {
      if (!this.fromDate || !this.toDate) return ''
      for (const preset of this.presets) {
        const range = this.rangeForPreset(preset.key)
        if (range && range.fromDate === this.fromDate && range.toDate === this.toDate) {
          return preset.key
        }
      }
      return ''
    },
    columns () {
      const cols = [{ key: 'date', label: 'Date' }]
      for (const name of this.userNames) {
        cols.push({ key: name, label: name, align: 'right', format: 'number' })
      }
      cols.push({ key: '_total', label: 'Total', align: 'right', format: 'number' })
      return cols
    },
    tableRows () {
      return this.callsRows.map((row) => {
        const next = { date: row.date }
        let total = 0
        for (const name of this.userNames) {
          const value = Number(row[name]) || 0
          next[name] = value
          total += value
        }
        next._total = total
        return next
      })
    },
    agentTotals () {
      const totals = {}
      for (const name of this.userNames) totals[name] = 0
      for (const row of this.callsRows) {
        for (const name of this.userNames) {
          totals[name] += Number(row[name]) || 0
        }
      }
      return totals
    },
    topAgent () {
      let best = null
      for (const name of this.userNames) {
        const total = this.agentTotals[name] || 0
        if (!best || total > best.total) best = { name, total }
      }
      return best && best.total > 0 ? best : null
    },
    ordersPerDayLabel () {
      if (!this.summary || !this.callsRows.length) return 'Across the period'
      const avg = (Number(this.summary.orders_total) || 0) / this.callsRows.length
      return `${avg.toFixed(1)} / day average`
    },
    bottlesPerOrderLabel () {
      if (!this.summary) return 'Across the period'
      const orders = Number(this.summary.orders_total) || 0
      const bottles = Number(this.summary.bottles_total) || 0
      if (!orders) return 'Across the period'
      return `${(bottles / orders).toFixed(2)} / order`
    }
  },
  created () {
    this.loadReport()
  },
  methods: {
    authToken () {
      return localStorage.getItem('api_token')
    },
    toIsoStatic (date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    toIso (date) {
      return this.toIsoStatic(date)
    },
    formatDisplay (iso) {
      if (!iso) return ''
      const [y, m, d] = iso.split('-')
      if (!y || !m || !d) return iso
      return `${d}-${m}-${y}`
    },
    formatNumber (value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toLocaleString('en-IN')
    },
    parsePercent (raw) {
      if (raw == null) return null
      const n = parseFloat(String(raw).replace(/[^0-9.+-]/g, ''))
      if (!Number.isFinite(n)) return null
      return n <= 1 ? n * 100 : n
    },
    formatPercent (value) {
      const n = this.parsePercent(value)
      if (n == null) return '—'
      return `${n.toFixed(2)}%`
    },
    rangeForPreset (key) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const start = new Date(today)
      const end = new Date(today)
      switch (key) {
        case 'today':
          break
        case '7d':
          start.setDate(today.getDate() - 6)
          break
        case '30d':
          start.setDate(today.getDate() - 29)
          break
        case 'mtd':
          start.setDate(1)
          break
        case 'ytd':
          start.setMonth(0, 1)
          break
        default:
          return null
      }
      return { fromDate: this.toIso(start), toDate: this.toIso(end) }
    },
    applyPreset (key) {
      const range = this.rangeForPreset(key)
      if (!range) return
      this.fromDate = range.fromDate
      this.toDate = range.toDate
    },
    loadReport () {
      this.errorMessage = ''
      this.isLoading = true
      axios.post(
        `${this.$apiAdress}/api/reports/telesales?token=${this.authToken()}`,
        { from_date: this.fromDate, to_date: this.toDate }
      )
        .then((response) => {
          const data = response.data || {}
          this.summary = data
          this.callsRows = Array.isArray(data.calls) ? data.calls : []
          const first = this.callsRows[0] || {}
          this.userNames = Object.keys(first).filter((k) => k !== 'date')
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load telesales report.'
          this.summary = null
          this.callsRows = []
          this.userNames = []
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>

<style scoped>
.telesales-report {
  padding: 0.25rem 0;
}

.ts-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.ts-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.ts-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.ts-toolbar__title p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.ts-toolbar__accent {
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.ts-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.ts-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.ts-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.55rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
  flex-wrap: wrap;
}

.ts-section__head-left {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  min-width: 0;
}

.ts-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.ts-section__hint {
  color: #6b7280;
  font-size: 0.78rem;
}

.ts-section__body {
  padding: 1rem;
}

.ts-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.ts-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.ts-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ts-field--range {
  flex: 0 1 360px;
  min-width: 260px;
}

.ts-field--presets {
  flex: 1 1 320px;
  min-width: 0;
}

.ts-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  min-height: 36px;
  align-items: center;
}

.ts-preset {
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 0.85rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.ts-preset:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.ts-preset:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.ts-preset.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
}

.ts-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  height: 36px;
  padding: 0 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.ts-btn--primary {
  color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.ts-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.ts-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.28);
}

.ts-btn:disabled,
.ts-btn.is-loading {
  opacity: 0.85;
  cursor: progress;
}

.ts-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
  margin-bottom: 1rem;
}

.kpi-tile {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  padding: 0.85rem 1rem;
  border-left-width: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.kpi-tile--calls      { border-left-color: #2563eb; }
.kpi-tile--orders     { border-left-color: #0e5b46; }
.kpi-tile--bottles    { border-left-color: #f59e0b; }
.kpi-tile--conversion { border-left-color: #7c3aed; }

.kpi-tile__label {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}

.kpi-tile__value {
  font-size: 1.55rem;
  font-weight: 800;
  color: #1f2a44;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}

.kpi-tile--orders .kpi-tile__value { color: #0e5b46; }
.kpi-tile--conversion .kpi-tile__value { color: #5b21b6; }

.kpi-tile__sub {
  font-size: 0.74rem;
  color: #6b7280;
  margin-top: 2px;
}

.ts-totals {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.ts-totals__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #4f5560;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}

.ts-totals__chip strong {
  color: #1f2a44;
  font-variant-numeric: tabular-nums;
}

.ts-totals__chip--brand {
  background: #dff5ea;
  border-color: #c8e6d8;
  color: #0e5b46;
}

.ts-totals__chip--brand strong {
  color: #0e5b46;
}

.ts-total-cell {
  font-variant-numeric: tabular-nums;
  color: #1f2a44;
}

@media (max-width: 991px) {
  .ts-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575px) {
  .ts-kpis {
    grid-template-columns: 1fr;
  }
}
</style>
